// FNP-1248 — mirrors the deployed FNP-1179 / FNP-1181 / FNP-1182 schema
// (flexi-domains-api/src/appsync-api/src/schema/features/provider-connections.graphql
//  and .../dns-providers.graphql).
//
// ⚠️ THE ONE RULE THAT GOVERNS EVERY TYPE IN THIS FILE: NO CREDENTIAL MATERIAL
// IS REPRESENTABLE HERE. There is no `apiToken` field on any OUTPUT type, and
// there cannot be — the schema has none, because a credential input type in
// GraphQL has no output counterpart by construction. `ConnectProviderCredential`
// below is the ONLY type carrying a token, it is an INPUT shape, and it is
// consumed by the service and discarded. Nothing stores it, caches it or
// returns it. If a field carrying a secret ever appears in this file, the
// guarantee the schema makes has been broken on the client side.

// ─── THE CATALOG (FNP-1179) ───

// What a KIND of provider requires. Mirrors `DnsProviderCredentialType`.
//
// ⚠️ THIS IS THE DISCRIMINATOR THE CONNECT FORM SWITCHES ON. A provider
// declares which shape of credential a connection to it must supply, and the
// form renders a role-ARN field or a token field accordingly. Rendering both
// and letting the tenant choose would let them submit a token for Route 53,
// which the adapter rejects at connect time — a failure the form can prevent by
// simply not offering the wrong field.
export type DnsProviderCredentialType = 'ASSUMED_ROLE' | 'API_TOKEN';

// What a kind of provider is capable of AT ALL. Every field non-null: the
// schema is explicit that a provider whose capabilities are unknown does not
// belong in the catalog, because null would be indistinguishable from "cannot".
export type DnsProviderCapabilities = {
    supportedRecordTypes: string[],
    canWriteRecords: boolean,
    canCreateZones: boolean,
    canDeleteZones: boolean,
    canSetNameservers: boolean,
}

export type DnsProvider = {
    // Stable catalog identifier, e.g. 'ROUTE53' / 'CLOUDFLARE'.
    id: string,
    // Human-readable, e.g. 'Amazon Route 53'. The picker shows THIS, never id.
    name: string,
    credentialType: DnsProviderCredentialType,
    // ⚠️ The CEILING. A connection's own snapshot is this narrowed by what the
    // tenant's credential actually proved — never wider.
    capabilities: DnsProviderCapabilities,
}

export type ListDnsProviders = {
    listDnsProviders: {
        items: DnsProvider[],
        nextToken?: string | null,
    }
}

// ─── THE CONNECTION (FNP-1181) ───

// Which shape of credential THIS TENANT ACTUALLY SUPPLIED. A separate enum from
// the catalog's in the schema, and separate here, because they answer different
// questions — what a provider REQUIRES vs what was SUPPLIED.
export type ProviderConnectionCredentialType = 'ASSUMED_ROLE' | 'API_TOKEN';

// ⚠️ THREE STATES, AND `INVALID` IS NOT ALWAYS A MISTAKE.
//
//   ACTIVE      — validated against the provider; the snapshot is real.
//   INVALID     — the provider REJECTED the credential. NOT retryable on its
//                 own: something must change first.
//   UNREACHABLE — the provider could not be REACHED. The credential may be
//                 perfectly good. Retryable as-is.
//
// The schema is emphatic that there is no PENDING state, because creation
// validates before storing. That has a consequence this module is built around:
// a FIRST-TIME ASSUMED_ROLE connection lands INVALID or UNREACHABLE BY DESIGN
// (see `isAwaitingTrustPolicy` below), and rendering that as a user error would
// be wrong. See the module's ExternalIdPanel for the full explanation.
export type ProviderConnectionStatus = 'ACTIVE' | 'INVALID' | 'UNREACHABLE';

// What THIS CONNECTION'S CREDENTIAL proved it could do — the catalog ceiling
// narrowed by the credential. A tenant who handed Flexi a read-only Route 53
// role has a connection that cannot write, and that is a legitimate,
// fully-working connection, not a broken one. The detail view renders it as
// read-only rather than as failure.
export type ProviderConnectionCapabilities = {
    supportedRecordTypes: string[],
    canWriteRecords: boolean,
    canCreateZones: boolean,
    canDeleteZones: boolean,
    canSetNameservers: boolean,
}

// WHERE THE SECRET LIVES. Never the secret.
//
// ⚠️ THE TWO FIELDS HAVE OPPOSITE AUDIENCES AND THIS MODULE TREATS THEM SO.
//
//   secretArn  — infrastructure. An OPERATOR's address for the secret. The
//                schema notes it is not itself sensitive (knowing the address
//                of a safe does not open it), but a TENANT has no use for it
//                and it is deliberately NOT rendered anywhere in this module.
//                It is not even selected in the queries — see services/queries.
//
//   externalId — THE TENANT'S. Flexi MINTS it per connection and the tenant
//                MUST put it in their role's trust policy or the assume-role
//                can never succeed. It is a value Flexi has to TELL them, and
//                surfacing it prominently is the central job of this module's
//                detail view.
//
// Present only for ASSUMED_ROLE connections, hence nullable.
export type ProviderConnectionSecretRef = {
    externalId?: string | null,
}

export type ProviderConnection = {
    // Composite and tenant-scoped: 'PROVIDER_CONNECTION#<accountId>#<uuid>'.
    // ⚠️ OPAQUE. Nothing here parses or splits it; it is passed back verbatim.
    id: string,
    accountId?: string,
    // References DnsProvider.id from the catalog, e.g. 'ROUTE53'.
    providerId: string,
    // What the tenant named it, so two connections to the same provider are
    // tellable apart. Every list and picker shows this, never the id.
    displayName: string,
    credentialType: ProviderConnectionCredentialType,
    status: ProviderConnectionStatus,
    // ⚠️ THE PROVIDER'S OWN WORDS, VERBATIM. Never re-worded on the way to the
    // screen — classify for the core, preserve for the human (FNP-1180).
    // FNP-1087 took a whole investigation because a normalised class was all
    // anyone had.
    statusDetail?: string | null,
    lastValidatedAt?: string | null,
    capabilities: ProviderConnectionCapabilities,
    // ⚠️ FNP-1233 — DECLARED, NOT PROBED. See `writable` on ConnectProviderInput
    // for the full reasoning and the deployment caveat.
    //
    // Optional HERE (unlike the `Boolean!` the API will ship) for one reason:
    // this field is NOT in the deployed schema at the time of writing, so a
    // connection read from the live API comes back without it. Typing it
    // non-null would be a lie the compiler enforces and the runtime breaks.
    //
    // ⚠️ Read it through `isWritableDeclared()`, never directly. `undefined`
    // means "this API does not report the field yet", which must resolve to
    // NOT-writable — the same direction as an explicit false, so an
    // undeployed schema fails closed rather than granting writes by omission.
    writable?: boolean | null,
    secretRef: ProviderConnectionSecretRef,
    createdByUsername?: string | null,
    created?: string | null,
    updated?: string | null,
}

export type ProviderConnectionsConnectionResult = {
    items: ProviderConnection[],
    nextToken?: string | null,
}

export type ListProviderConnections = {
    listProviderConnections: ProviderConnectionsConnectionResult
}

export type GetProviderConnection = {
    getProviderConnection: ProviderConnection
}

// ─── THE WRITE VOCABULARY (FNP-1182) ───

// ⚠️ THE ONLY TYPE IN THIS FILE THAT CARRIES CREDENTIAL MATERIAL, AND IT IS AN
// INPUT. It travels from the form, through the service, into the mutation
// variables, and is never assigned to any state that outlives the submit.
//
// Exactly ONE field is populated, chosen by the provider's credentialType:
//   ASSUMED_ROLE → roleArn    API_TOKEN → apiToken
//
// Both are optional in the schema because the discriminator lives on the
// provider, not on this shape. The form guarantees the right one.
export type ConnectProviderCredential = {
    // The role in the TENANT'S OWN account. Not a secret — it is an identifier,
    // and the detail view shows it back alongside the external id precisely so
    // the tenant can check it against their trust policy.
    roleArn?: string,
    // ⚠️ WRITE-ONLY, ALWAYS. Never rendered, never logged, never echoed back,
    // never put in a query cache. There is no query that could return it.
    apiToken?: string,
}

export type ConnectProviderInput = {
    providerId: string,
    displayName: string,
    credential: ConnectProviderCredential,
    // ⚠️ FNP-1233 — THE TENANT'S CLAIM ABOUT THEIR OWN CREDENTIAL.
    //
    // Write capability is DECLARED, not probed. This is the tenant telling
    // Flexi what the role they created can do — "this role can create and
    // delete zones" — not a Flexi permission being granted and not a request
    // for one. The distinction is the whole reason the label matters: a control
    // reading "enable writes" invites someone to tick it hopefully, and a
    // hopeful tick produces a connection that claims a capability the
    // credential does not have, failing at the first write instead of at the
    // declaration.
    //
    // ⚠️ DEFAULTS OFF, HERE AND SERVER-SIDE. Nullable in the API with a
    // server-side default of false, so a connection nobody deliberately marked
    // writable cannot write. Omitting the field is a valid way to say "no".
    //
    // ⚠️ NOT IN THE DEPLOYED SCHEMA YET (see the report accompanying this
    // work). It is sent only when explicitly true — see buildConnectInput().
    writable?: boolean,
}

export type ConnectProvider = {
    connectProvider: ProviderConnection
}

// Test a credential and store NOTHING.
//
// ⚠️ THIS CANNOT VALIDATE A REAL ASSUMED_ROLE ASSUMPTION, AND THE MODULE MUST
// NOT IMPLY IT DOES. The external id it mints is a THROWAWAY: never stored,
// never returned. The tenant's trust policy names the id from their REAL
// connection, which does not exist yet at test time — so a Route 53 test can
// only ever come back INVALID for a tenant who has not yet connected.
//
// It is therefore used here for ONE thing: catching a malformed ARN or a
// wrong-shaped credential BEFORE saving. See hooks/useTestProviderCredential
// for the guard that keeps it honest.
export type TestProviderCredentialInput = {
    providerId: string,
    credential: ConnectProviderCredential,
}

// NOT a ProviderConnection — nothing was stored, and a connection-shaped result
// would misrepresent what happened. Same three-state status vocabulary, so one
// component renders a test result and a real connection's health.
export type ProviderCredentialTestResult = {
    ok: boolean,
    status: ProviderConnectionStatus,
    statusDetail?: string | null,
    capabilities: ProviderConnectionCapabilities,
}

export type TestProviderCredential = {
    testProviderCredential: ProviderCredentialTestResult
}

// Replace the credential, KEEP the connection.
//
// ⚠️ THIS IS THE RE-VALIDATE PATH, and it is the whole reason a first INVALID
// is recoverable. An ASSUMED_ROLE connection keeps its external id across a
// rotation, so the tenant fixes their trust policy and re-submits THE SAME
// roleArn — no trust-policy edit is needed on Flexi's side.
//
// No `providerId`: the provider is a property of the connection being rotated,
// and accepting one would let a caller move a connection between providers
// under the guise of a credential change.
export type RotateProviderCredentialInput = {
    id: string,
    credential: ConnectProviderCredential,
    // ⚠️ FNP-1233 — ROTATION IS ALSO THE PROMOTION PATH.
    //
    // This is how a connection becomes writable after its trust policy is
    // fixed: the tenant re-submits the same roleArn and declares that the role
    // can now write. Same semantics and same default-off rule as on connect —
    // see ConnectProviderInput.
    writable?: boolean,
}

export type RotateProviderCredential = {
    rotateProviderCredential: ProviderConnection
}

// ─── DISCONNECT (FNP-1182) ───

// What disconnecting WOULD destroy. Read before it is destroyed.
export type ProviderDisconnectPreview = {
    connectionId: string,
    displayName: string,
    providerId: string,
    // The connection's status at preview time, so a broken connection is
    // visibly broken on the confirmation screen rather than looking empty.
    status: ProviderConnectionStatus,
    // ⚠️ NULL IS "UNKNOWN", NOT "ZERO". THIS IS THE MOST DANGEROUS FIELD IN THE
    // MODULE.
    //
    // A broken connection cannot be asked what it manages. Rendering null as
    // "0 zones" would tell a tenant that disconnecting costs them nothing when
    // it might cost them every zone they have — the empty-versus-broken
    // confusion at the one moment it is most expensive. `zoneCountUnavailable`
    // states the unknown explicitly so a client cannot mistake null for "the
    // field was not requested". Everything that renders this branches on the
    // FLAG FIRST — see `zoneImpact()` below, which is the only reader.
    managedZoneCount?: number | null,
    zoneCountUnavailable: boolean,
    secretName: string,
    // Plain sentences, most severe first. Rendered VERBATIM and in order — this
    // is read by a human deciding whether to proceed.
    consequences: string[],
}

export type PreviewProviderDisconnect = {
    previewProviderDisconnect: ProviderDisconnectPreview
}

export type DisconnectProviderInput = {
    id: string,
}

// Every field is an OBSERVED FACT, not an intention: `secretDeleted` is true
// only when Secrets Manager confirmed the delete.
export type DisconnectProviderResult = {
    connectionId: string,
    secretDeleted: boolean,
    secretName: string,
    connectionDeleted: boolean,
}

export type DisconnectProvider = {
    disconnectProvider: DisconnectProviderResult
}

// ─── THE DERIVED VOCABULARY ───
//
// Named states rather than nullability checks scattered across the table, the
// detail view and the disconnect modal. Three call sites branching on the same
// nullable field independently is how two of them end up agreeing and the third
// does not — the lesson the zones module's `delegationState` records.

// THE ZONE-IMPACT TRI-STATE, NAMED.
//
// ⚠️ THE FLAG IS CHECKED BEFORE THE NUMBER, ALWAYS. Reading `managedZoneCount`
// first and treating a null as 0 is the exact defect this type exists to make
// unrepresentable.
export type ZoneImpact =
    | { kind: 'UNKNOWN' }
    | { kind: 'NONE' }
    | { kind: 'COUNTED', count: number };

export const zoneImpact = (preview: ProviderDisconnectPreview): ZoneImpact => {
    // FLAG FIRST. An unavailable count is unknown whatever the number field
    // happens to hold — including when it holds 0.
    if (preview.zoneCountUnavailable) {
        return {kind: 'UNKNOWN'};
    }
    const count = preview.managedZoneCount;
    // A null with the flag UNSET should not happen, but "should not happen" is
    // not a reason to render it as zero. Unknown is the honest answer.
    if (typeof count !== 'number') {
        return {kind: 'UNKNOWN'};
    }
    if (count === 0) {
        return {kind: 'NONE'};
    }
    return {
        kind: 'COUNTED',
        count,
    };
};

/**
 * Whether this connection is a FIRST-TIME ASSUMED_ROLE connection that is
 * simply waiting for the tenant to paste the external id into their trust
 * policy — as opposed to one that is genuinely misconfigured.
 *
 * ⚠️ THIS IS THE CENTRAL DISTINCTION OF THE WHOLE MODULE.
 *
 * Flexi mints the external id SERVER-SIDE during `connectProvider`, derived
 * from the connection id — so it cannot exist before the connection does. The
 * tenant needs it in their role's trust policy before Flexi can assume the
 * role. Therefore the ordering is unavoidable:
 *
 *   1. Connect → stored, status INVALID/UNREACHABLE, externalId now populated.
 *   2. Flexi TELLS the tenant the external id + role ARN.
 *   3. Tenant edits their trust policy, then re-validates (rotate).
 *
 * Step 1 coming back not-ACTIVE is the DESIGNED path, not a failure. Presenting
 * it as "something went wrong" tells the tenant they made a mistake when they
 * did exactly what was asked, and hides the one value they now need.
 *
 * The condition is deliberately NOT time-based or attempt-counted: an
 * ASSUMED_ROLE connection that is not ACTIVE and HAS an external id always has
 * "add this to your trust policy" as its next step, whether it is thirty
 * seconds or thirty days old. A tenant returning to an old broken connection
 * needs that instruction just as much.
 */
export const isAwaitingTrustPolicy = (connection: ProviderConnection): boolean => {
    return connection.credentialType === 'ASSUMED_ROLE'
        && connection.status !== 'ACTIVE'
        && Boolean(connection.secretRef?.externalId);
};

/**
 * Whether a connection works at all right now.
 *
 * Used to gate the capability rendering: an INVALID connection's snapshot
 * records what the credential proved AT CONNECT TIME, which is not a claim
 * about what it can do NOW. The detail view says so rather than presenting a
 * stale snapshot as current fact.
 */
export const isConnectionUsable = (connection: ProviderConnection): boolean => {
    return connection.status === 'ACTIVE';
};

/**
 * Whether a not-ACTIVE status can plausibly be fixed by retrying AS-IS.
 *
 * UNREACHABLE means the provider could not be reached — the credential may be
 * perfectly good, so re-validating unchanged is a sensible thing to offer.
 * INVALID means the provider REJECTED it: clicking again changes nothing, and
 * the tenant must fix something (usually the trust policy) first. The
 * re-validate control words itself differently for the two.
 */
export const isStatusRetryableAsIs = (status: ProviderConnectionStatus): boolean => {
    return status === 'UNREACHABLE';
};

// ─── FNP-1233: DECLARED WRITE CAPABILITY ───

/**
 * Whether this connection was DECLARED writable by the tenant.
 *
 * ⚠️ FAILS CLOSED ON EVERY UNKNOWN, AND THAT IS THE POINT.
 *
 * Three inputs collapse to false: an explicit `false`, a `null`, and —
 * critically — `undefined`, which is what every connection currently returns
 * because `writable` is NOT in the deployed schema yet. Reading the field
 * directly (`if (connection.writable)`) would happen to behave correctly today
 * and start granting writes the moment someone changed the type to non-null
 * without checking the API. Routing every read through here makes the
 * fail-closed direction a property of the module rather than of each call site.
 *
 * ⚠️ This answers "did the tenant claim this credential can write", NOT "does
 * Flexi permit writing". Nothing here grants anything.
 */
export const isWritableDeclared = (connection: ProviderConnection): boolean => {
    return connection.writable === true;
};

/**
 * Build the `connectProvider` input, omitting `writable` unless it is true.
 *
 * ⚠️ THE OMISSION IS DELIBERATE AND LOAD-BEARING WHILE FNP-1233 IS IN FLIGHT.
 *
 * `writable` is not yet on the deployed `ConnectProviderInput`, and AppSync
 * REJECTS a variable the schema does not declare — it is a validation error for
 * the whole request, not an ignored extra. Sending `writable: false` on every
 * connect would therefore break the connect flow entirely against the current
 * API, for the sake of transmitting the value the server already defaults to.
 *
 * So: false and undefined both mean "not writable" and are both expressed by
 * SAYING NOTHING, which is exactly what the API's nullable-with-false-default
 * contract means. Only an affirmative declaration is transmitted. When the
 * schema ships, a `true` starts being honoured and nothing here has to change;
 * until then a `true` fails loudly against the undeployed field rather than
 * silently producing a connection that claims a capability it was never
 * granted.
 */
export const buildConnectInput = (
    providerId: string,
    displayName: string,
    credential: ConnectProviderCredential,
    writable: boolean,
): ConnectProviderInput => {
    const input: ConnectProviderInput = {
        providerId,
        displayName,
        credential,
    };
    if (writable) {
        input.writable = true;
    }
    return input;
};

/**
 * Build the `rotateProviderCredential` input. Same omission rule, same reason
 * as `buildConnectInput` — see there.
 */
export const buildRotateInput = (
    id: string,
    credential: ConnectProviderCredential,
    writable: boolean,
): RotateProviderCredentialInput => {
    const input: RotateProviderCredentialInput = {
        id,
        credential,
    };
    if (writable) {
        input.writable = true;
    }
    return input;
};
